import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Loader2, Image as ImageIcon } from "lucide-react";

/**
 * Multi-photo uploader. Calls /api/upload sequentially for each picked file.
 *
 * Props:
 *  - kind: upload kind (e.g. "inst_event_photo")
 *  - photos: array of { url, public_id }
 *  - onChange: (next: Array<{url, public_id}>) => void
 *  - max: max photos (default 10)
 *  - maxBytes: per-file size cap
 *  - humanMax: human-readable size cap
 *  - testid: prefix for data-testid
 */
export default function MultiPhotoUploader({
  kind,
  photos = [],
  onChange,
  max = 10,
  maxBytes = 100 * 1024,
  humanMax = "100 KB",
  testid = "multi-photo",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const remaining = Math.max(0, max - photos.length);

  const pick = () => {
    if (remaining === 0) return;
    inputRef.current?.click();
  };

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;

    const slots = Math.max(0, max - photos.length);
    const toUpload = files.slice(0, slots);
    if (files.length > slots) {
      toast.warning(
        `Only ${slots} more photo${slots === 1 ? "" : "s"} can be added (max ${max}).`,
      );
    }

    setBusy(true);
    const added = [];
    for (const file of toUpload) {
      if (file.size > maxBytes) {
        toast.error(`"${file.name}" is too large. Max ${humanMax}`);
        continue;
      }
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push({ url: data.url, public_id: data.public_id });
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err?.response?.data?.detail) || `Upload of "${file.name}" failed`,
        );
      }
    }
    setBusy(false);
    if (added.length > 0) {
      onChange([...photos, ...added]);
      toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} added`);
    }
  };

  const removeAt = (idx) => {
    const next = photos.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testid}-wrapper`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp"
        multiple
        className="hidden"
        onChange={handle}
        data-testid={`${testid}-input`}
      />

      {photos.length === 0 ? (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid={`${testid}-pick-empty`}
          className="w-full border border-dashed border-[#e5e0d8] hover:border-[#234333]/50 rounded-sm bg-white p-6 flex items-center gap-3 text-left transition-colors disabled:opacity-60"
        >
          <div className="w-10 h-10 rounded-sm bg-[#f9f8f6] border border-[#e5e0d8] flex items-center justify-center text-[#234333]">
            {busy ? (
              <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
            ) : (
              <ImageIcon size={18} strokeWidth={1.5} />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm text-[#1c1a17]">
              {busy ? "Uploading…" : "Add event photos"}
            </div>
            <div className="text-[11px] text-[#6b655b]">
              JPG / PNG / WEBP · max {humanMax} per photo · up to {max} photos
            </div>
          </div>
        </button>
      ) : (
        <>
          <div
            className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2"
            data-testid={`${testid}-grid`}
          >
            {photos.map((p, idx) => (
              <div
                key={p.public_id || p.url || idx}
                data-testid={`${testid}-tile-${idx}`}
                className="relative aspect-square bg-[#f9f8f6] border border-[#e5e0d8] rounded-sm overflow-hidden group"
              >
                <img
                  src={p.url}
                  alt={`photo-${idx}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  data-testid={`${testid}-remove-${idx}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-white/90 hover:bg-white text-[#9e2a2b] border border-[#e5e0d8] flex items-center justify-center"
                  aria-label={`Remove photo ${idx + 1}`}
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            ))}
            {remaining > 0 && (
              <button
                type="button"
                onClick={pick}
                disabled={busy}
                data-testid={`${testid}-pick-more`}
                className="aspect-square border border-dashed border-[#e5e0d8] hover:border-[#234333]/60 rounded-sm bg-white flex flex-col items-center justify-center text-[#234333] text-[11px] disabled:opacity-60"
              >
                {busy ? (
                  <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
                ) : (
                  <>
                    <Plus size={20} strokeWidth={1.5} />
                    <span className="mt-1">Add</span>
                  </>
                )}
              </button>
            )}
          </div>
          <div className="text-[11px] text-[#6b655b]">
            {photos.length} of {max} photos · JPG / PNG / WEBP · max {humanMax} each
          </div>
        </>
      )}
    </div>
  );
}
